import { useEffect, useState } from "react";
import { CheckCircle2, ChevronDown, ChevronUp, Flag, MessageSquare, Target, Trophy } from "lucide-react";
import api from "@/lib/api";
import { Link } from "react-router-dom";

/**
 * Compiled RHYTHM packet viewer used inside LIFT screens.
 * Auto-fetches the LIFT cycle to determine the (period_start, period_end)
 * window then pulls the compiled packet from RHYTHM.
 */
export default function LiftPacketPanel({ cycleId, employeeId, defaultOpen = false }) {
  const [open, setOpen] = useState(defaultOpen);
  const [packet, setPacket] = useState(null);
  const [meta, setMeta] = useState(null);
  const [loading, setLoading] = useState(false);
  const [errored, setErrored] = useState(false);

  useEffect(() => {
    if (!open || packet) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const { data: cycle } = await api.get(`/lift/cycles/${cycleId}`);
        if (cancelled) return;
        const { data } = await api.get(`/rhythm/lift-packet`, {
          params: {
            employee_id: employeeId,
            cycle_start: cycle.period_start,
            cycle_end: cycle.period_end,
          },
        });
        if (cancelled) return;
        setPacket(data?.packet || null);
        setMeta({ compiled_at: data?.compiled_at, compiled_by_name: data?.compiled_by_name });
      } catch {
        if (!cancelled) setErrored(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open, cycleId, employeeId, packet]);

  return (
    <section className="patria-card p-6" data-testid="lift-rhythm-packet-panel">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between gap-3 text-left"
        data-testid="lift-rhythm-packet-toggle"
      >
        <div className="flex items-start gap-3">
          <div className="h-9 w-9 rounded-full bg-terracotta-500/15 text-terracotta-600 flex items-center justify-center shrink-0">
            <MessageSquare className="h-4 w-4" strokeWidth={1.5} />
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-widest text-terracotta-500 font-semibold">
              RHYTHM
            </p>
            <h3 className="font-serif text-2xl text-forest-900">1:1 Compiled Packet</h3>
            <p className="text-xs text-charcoal-muted mt-1">
              Meetings, closed action items, goal progress, wins and direct-feedback flags
              from the RHYTHM 1:1 record for this review period.
            </p>
          </div>
        </div>
        <div className="text-forest-700">
          {open ? <ChevronUp className="h-5 w-5" strokeWidth={1.5} /> : <ChevronDown className="h-5 w-5" strokeWidth={1.5} />}
        </div>
      </button>

      {open && (
        <div className="mt-4">
          {loading && <p className="italic text-charcoal-muted">Compiling packet…</p>}
          {errored && !loading && (
            <p className="text-sm text-charcoal-soft italic">
              Couldn&rsquo;t load the RHYTHM packet — no active pairing yet, or you don&rsquo;t have access.
            </p>
          )}
          {!loading && !errored && !packet && (
            <p className="text-sm text-charcoal-soft italic">
              No RHYTHM data compiled for this review window yet.
            </p>
          )}
          {packet && (
            <div className="space-y-4">
              {meta?.compiled_at && (
                <p className="text-[10px] uppercase tracking-widest text-charcoal-muted">
                  Compiled {(meta.compiled_at || "").slice(0, 10)}
                  {meta.compiled_by_name ? ` · by ${meta.compiled_by_name}` : ""}
                </p>
              )}

              {/* Meetings */}
              <div>
                <h4 className="text-sm font-medium text-forest-900 flex items-center gap-1.5">
                  <MessageSquare className="h-4 w-4 text-forest-700" strokeWidth={1.5} />
                  Meetings ({packet.meetings.length})
                </h4>
                <ul className="mt-2 space-y-2">
                  {packet.meetings.length === 0 && (
                    <li className="text-xs italic text-charcoal-muted">No finalized 1:1s in this window.</li>
                  )}
                  {packet.meetings.map((m) => (
                    <li key={m.id} className="p-3 rounded-xl bg-cream-100">
                      <div className="flex items-center justify-between gap-3">
                        <p className="text-sm font-medium text-forest-900">{m.date}</p>
                        <div className="flex items-center gap-2">
                          {m.direct_feedback && (
                            <span className="patria-chip-terra text-[10px]">
                              <Flag className="h-3 w-3 mr-0.5" strokeWidth={1.5} /> Direct Feedback
                            </span>
                          )}
                          {m.acknowledged && (
                            <span className="patria-chip-green text-[10px]">Acknowledged</span>
                          )}
                        </div>
                      </div>
                      {m.topics.length > 0 && (
                        <p className="text-xs text-charcoal-muted mt-1">
                          {m.topics.slice(0, 4).join(" · ")}{m.topics.length > 4 ? "…" : ""}
                        </p>
                      )}
                    </li>
                  ))}
                </ul>
              </div>

              {/* Direct feedback flagged */}
              {(packet.direct_feedback_flags || []).length > 0 && (
                <div>
                  <h4 className="text-sm font-medium text-forest-900 flex items-center gap-1.5">
                    <Flag className="h-4 w-4 text-terracotta-600" strokeWidth={1.5} />
                    Direct Feedback Flagged ({packet.direct_feedback_flags.length})
                  </h4>
                  <ul className="mt-2 space-y-2">
                    {packet.direct_feedback_flags.map((f) => (
                      <li key={f.meeting_id} className="p-3 rounded-xl bg-terracotta-500/5 border-l-2 border-terracotta-500">
                        <p className="text-sm text-forest-900">{f.topic_note || "(no topic)"}</p>
                        <p className="text-[10px] uppercase tracking-widest text-charcoal-muted mt-1">
                          {f.date} · Tags: {f.tags.join(", ") || "—"}
                        </p>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Closed action items */}
              <div>
                <h4 className="text-sm font-medium text-forest-900 flex items-center gap-1.5">
                  <CheckCircle2 className="h-4 w-4 text-forest-700" strokeWidth={1.5} />
                  Closed Action Items ({packet.closed_action_items.length})
                </h4>
                <ul className="mt-2 space-y-1">
                  {packet.closed_action_items.length === 0 && (
                    <li className="text-xs italic text-charcoal-muted">None closed in this window.</li>
                  )}
                  {packet.closed_action_items.slice(0, 8).map((a) => (
                    <li key={a.id} className="text-sm text-forest-900">
                      &middot; {a.title} <span className="text-[10px] text-charcoal-muted">({(a.completed_at || "").slice(0, 10)})</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Goals */}
              <div>
                <h4 className="text-sm font-medium text-forest-900 flex items-center gap-1.5">
                  <Target className="h-4 w-4 text-forest-700" strokeWidth={1.5} />
                  Goal Progress ({packet.goal_progress.length})
                </h4>
                <ul className="mt-2 space-y-1">
                  {packet.goal_progress.length === 0 && (
                    <li className="text-xs italic text-charcoal-muted">No goals in this window.</li>
                  )}
                  {packet.goal_progress.map((g) => (
                    <li key={g.id} className="text-sm text-forest-900">
                      &middot; {g.description}
                      <span className="text-[10px] text-charcoal-muted ml-1">({g.status.replaceAll("_", " ")})</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Wins */}
              <div>
                <h4 className="text-sm font-medium text-forest-900 flex items-center gap-1.5">
                  <Trophy className="h-4 w-4 text-terracotta-500" strokeWidth={1.5} />
                  Wins ({packet.wins.length})
                </h4>
                <ul className="mt-2 space-y-1">
                  {packet.wins.length === 0 && (
                    <li className="text-xs italic text-charcoal-muted">No wins logged in this window.</li>
                  )}
                  {packet.wins.slice(0, 8).map((w) => (
                    <li key={w.id} className="text-sm text-forest-900">
                      &middot; {w.text} <span className="text-[10px] text-charcoal-muted">({w.dated_on})</span>
                    </li>
                  ))}
                </ul>
              </div>

              <Link
                to={`/rhythm/growth/${employeeId}`}
                className="patria-btn-secondary text-sm inline-flex"
                data-testid="lift-open-rhythm-workspace"
              >
                Open Full RHYTHM Growth Plan
              </Link>
            </div>
          )}
        </div>
      )}
    </section>
  );
}
